/**
 * Plan requests — agencies asking to be switched back on.
 *
 * This is the queue that replaced a `mailto:` link. An agency whose trial or
 * paid period lapsed picks a cycle and the add-ons they want; it lands here.
 *
 * **Approving applies exactly what they chose.** There is a deliberate absence
 * of editable fields for the cycle and the add-ons: retyping them out of an
 * email is how a customer ends up on the wrong plan, and the amount is the one
 * they were quoted rather than a fresh calculation against a price list that
 * may have moved. What an operator *does* fill in is the payment — method and
 * reference — because that is the part the system genuinely does not know.
 *
 * There is no payment gateway yet. When there is, approving becomes automatic
 * and this queue holds only the ones that need a human.
 */
import { useState } from 'react';
import { CheckCircle, Prohibit, Receipt } from '@phosphor-icons/react';
import { Link } from 'react-router-dom';
import {
  useApprovePlanRequest,
  useDeclinePlanRequest,
  usePlanRequests,
} from '../queries/platform';
import type { PlanRequest, PlanRequestStatus } from '../lib/types';
import { apiError } from '../lib/api';
import { inr, relativeDate } from '../lib/format';
import {
  Avatar,
  Badge,
  DefinitionList,
  EmptyState,
  ErrorState,
  Field,
  LoadingBlock,
  Modal,
  Segmented,
  SubmitButton,
  type Tone,
} from '../components/ui';

const COLUMNS = '1.8fr 1fr 1.6fr 1fr 1.4fr';

const STATUS_TONE: Record<PlanRequestStatus, Tone> = {
  pending: 'info',
  approved: 'ok',
  declined: 'off',
};

/** The add-on keys as names. The API sends keys; the catalogue lives there. */
const addOnLabel = (key: string) =>
  key
    .split('-')
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');

export default function PlanRequests() {
  const [status, setStatus] = useState<PlanRequestStatus | 'all'>('pending');
  const { data, isPending, isError, refetch } = usePlanRequests(status);
  const approve = useApprovePlanRequest();
  const decline = useDeclinePlanRequest();

  const [approving, setApproving] = useState<PlanRequest | null>(null);
  const [declining, setDeclining] = useState<PlanRequest | null>(null);
  const [method, setMethod] = useState('bank-transfer');
  const [reference, setReference] = useState('');
  const [reason, setReason] = useState('');
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');

  const rows = data?.data ?? [];
  const counts = data?.counts;

  const doApprove = async () => {
    if (!approving) return;
    setError('');
    try {
      const res = await approve.mutateAsync({ id: approving._id, method, reference });
      setNotice(res.message);
      setApproving(null);
      setReference('');
    } catch (err) {
      setError(apiError(err));
    }
  };

  const doDecline = async () => {
    if (!declining) return;
    setError('');
    try {
      const res = await decline.mutateAsync({ id: declining._id, reason });
      setNotice(res.message);
      setDeclining(null);
      setReason('');
    } catch (err) {
      setError(apiError(err));
    }
  };

  return (
    <div className="animate-up">
      {(notice || error) && (
        <div
          className="preview-note"
          style={{
            borderStyle: 'solid',
            borderColor: error ? 'var(--bad-line)' : 'var(--ok-line)',
            background: error ? 'var(--bad-bg)' : 'var(--ok-bg)',
            color: error ? 'var(--bad)' : 'var(--ok)',
          }}
        >
          <span>{error || notice}</span>
        </div>
      )}

      <div className="card">
        <div className="card-head">
          <Segmented
            value={status}
            onChange={setStatus}
            options={[
              { id: 'pending', label: 'Waiting', count: counts?.pending },
              { id: 'approved', label: 'Approved', count: counts?.approved },
              { id: 'declined', label: 'Declined', count: counts?.declined },
              { id: 'all', label: 'All' },
            ]}
          />
        </div>

        {isPending ? (
          <LoadingBlock label="Loading requests…" />
        ) : isError ? (
          <ErrorState message="Could not load plan requests." onRetry={() => void refetch()} />
        ) : rows.length === 0 ? (
          <EmptyState
            icon={<Receipt size={24} />}
            title={status === 'pending' ? 'Nothing waiting' : 'Nothing here'}
            message={
              status === 'pending'
                ? 'Every agency that asked to be switched back on has been dealt with.'
                : 'No requests with this status.'
            }
          />
        ) : (
          <div className="table-scroll">
            <div className="table">
              <div className="table-head" style={{ gridTemplateColumns: COLUMNS }}>
                <span>Agency</span>
                <span>Plan</span>
                <span>Extra features</span>
                <span>Total</span>
                <span style={{ textAlign: 'right' }}>Actions</span>
              </div>

              {rows.map((request) => (
                <div
                  key={request._id}
                  className="table-row"
                  style={{ gridTemplateColumns: COLUMNS }}
                >
                  <Link
                    to={`/agencies/${request.agency?._id}`}
                    className="row"
                    style={{ minWidth: 0, color: 'inherit' }}
                  >
                    <Avatar name={request.agency?.name} />
                    <div style={{ minWidth: 0 }}>
                      <p className="truncate strong" style={{ margin: 0, fontSize: 13.5 }}>
                        {request.agency?.name || 'Unknown agency'}
                      </p>
                      <p className="truncate t-xs muted" style={{ margin: '2px 0 0' }}>
                        {request.requestedByName || request.agency?.contactName || '—'} ·{' '}
                        {relativeDate(request.createdAt)}
                      </p>
                    </div>
                  </Link>

                  <div className="row row-tight">
                    <Badge tone={STATUS_TONE[request.status]}>
                      {request.billingCycle === 'annual' ? 'Annual' : 'Monthly'}
                    </Badge>
                  </div>

                  <span className="truncate t-sm">
                    {request.addOns.length ? request.addOns.map(addOnLabel).join(', ') : 'None'}
                  </span>

                  <span className="t-sm strong">{inr(request.amount)}</span>

                  <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 7 }}>
                    {request.status === 'pending' ? (
                      <>
                        <button
                          className="btn btn-sm btn-primary"
                          onClick={() => {
                            setError('');
                            setApproving(request);
                          }}
                        >
                          <CheckCircle size={14} /> Approve
                        </button>
                        <button
                          className="btn btn-sm"
                          onClick={() => {
                            setError('');
                            setDeclining(request);
                          }}
                        >
                          <Prohibit size={14} /> Decline
                        </button>
                      </>
                    ) : (
                      <Badge tone={STATUS_TONE[request.status]}>
                        {request.status === 'approved' ? 'Approved' : 'Declined'}
                      </Badge>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/*
        Approving is a confirmation, not a form for the plan.

        The cycle, the add-ons and the amount are shown read-only because they
        are the agency's decision and the figure they were quoted. Only the
        payment details are typed, and only because nothing else knows them.
      */}
      {approving && (
      <Modal
        onClose={() => setApproving(null)}
        title={`Switch ${approving?.agency?.name ?? ''} back on`}
        footer={
          <SubmitButton pending={approve.isPending} onClick={() => void doApprove()}>
            Confirm payment &amp; approve
          </SubmitButton>
        }
      >
        <p className="t-sm muted" style={{ margin: '0 0 14px', lineHeight: 1.6 }}>
          This applies exactly what they asked for and records the payment against their
          subscription. They are emailed to say the account is open again.
        </p>

        <DefinitionList
          rows={[
            ['Plan', approving?.billingCycle === 'annual' ? 'Annual' : 'Monthly'],
            [
              'Extra features',
              approving?.addOns.length ? approving.addOns.map(addOnLabel).join(', ') : 'None',
            ],
            ['Amount', approving ? inr(approving.amount) : ''],
            ['Their note', approving?.note || 'None'],
          ]}
        />

        <div style={{ marginTop: 14 }}>
          <Field label="How they paid">
            <select
              className="input"
              value={method}
              onChange={(e) => setMethod(e.target.value)}
            >
              <option value="bank-transfer">Bank transfer</option>
              <option value="upi">UPI</option>
              <option value="cheque">Cheque</option>
              <option value="cash">Cash</option>
              <option value="card">Card</option>
            </select>
          </Field>

          <Field
            label="Reference"
            hint="The number they will quote if they ever query this payment."
          >
            <input
              className="input"
              value={reference}
              onChange={(e) => setReference(e.target.value)}
              placeholder="e.g. NEFT-4821"
            />
          </Field>
        </div>
      </Modal>
      )}

      {declining && (
      <Modal
        onClose={() => setDeclining(null)}
        title={`Decline ${declining?.agency?.name ?? ''}`}
        footer={
          <SubmitButton
            pending={decline.isPending}
            className="btn btn-bad"
            onClick={() => void doDecline()}
          >
            Decline request
          </SubmitButton>
        }
      >
        <p className="t-sm muted" style={{ margin: '0 0 14px', lineHeight: 1.6 }}>
          Nothing changes about their subscription, and the agency is not emailed — a declined
          request is almost always a conversation already happening, and an automated notice
          arriving in the middle of it reads as a door closing.
        </p>
        <Field label="Reason" hint="The only record of why this was turned down.">
          <input
            className="input"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="e.g. duplicate of the one already paid"
          />
        </Field>
      </Modal>
      )}
    </div>
  );
}
